import pluralize from "pluralize"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"

import AlertBanner from "src/components/AlertBanner"
import Badge from "src/components/Badge"
import BoatPreviewLink from "src/components/BoatPreviewLink"
import CollapsibleBar from "src/components/CollapsibleBar"
import DataTableV2 from "src/components/DataTableV2"
import Loader from "src/components/Loader"

import { getDockwalkSummary, updateDockwalkFlag } from "src/api/DockwalkSummary"

import { useTracker } from "src/hooks/use_tracker"

import { statusBadgeColor } from "src/utils/status_badge_color_helper"
import { titlecase } from "src/utils/string_helpers"

import AuditTrailTable from "./AuditTrailTable"
import WalkActivityLog from "./WalkActivityLog"

const SummaryItems = ({ items }) => {
  const visibleItems = items.filter(Boolean)

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
      {visibleItems.map((item, index) => (
        <React.Fragment key={`${item.label}-${item.value}`}>
          <div className="flex items-center gap-1">
            <span className="font-semibold text-gray-900">{item.value}</span>
            <span>{item.label}</span>
          </div>
          {index < visibleItems.length - 1 && (
            <span className="text-lg text-gray-300">&bull;</span>
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

SummaryItems.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ).isRequired,
}

const CountBadge = ({ count }) => (
  <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
    {count}
  </span>
)

CountBadge.propTypes = {
  count: PropTypes.number.isRequired,
}

const DockwalkSummary = ({ marinaSlug, date }) => {
  const queryClient = useQueryClient()
  const tracker = useTracker()
  const [isFlaggedItemsOpen, setIsFlaggedItemsOpen] = useState(true)

  useEffect(() => {
    tracker.trackEvent("dockwalk_summary:viewed")
  }, [tracker])

  const summaryQuery = useQuery(
    ["dockwalkSummary", marinaSlug, date],
    () => getDockwalkSummary(marinaSlug, date),
    {
      enabled: Boolean(marinaSlug && date),
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  )

  const resolveFlagMutation = useMutation(
    (spaceNoteId) => updateDockwalkFlag(marinaSlug, spaceNoteId, false),
    {
      onSuccess: () => {
        tracker.trackEvent("dockwalk_summary:flag_resolved")
        queryClient.invalidateQueries(["dockwalkSummary", marinaSlug, date])
        queryClient.invalidateQueries(["dockwalkActivityLog", marinaSlug, date])
        queryClient.invalidateQueries(["dockwalkAuditTrail", marinaSlug, date])
      },
    }
  )

  const summary = summaryQuery.data
  const summaryCards = summary?.summaryCards || {}
  const flaggedNotes = useMemo(() => summary?.flaggedNotes || [], [summary])
  const walkProgress = useMemo(() => summary?.walkProgress || [], [summary])
  const spaceGroups = useMemo(() => summary?.spaceGroups || [], [summary])
  const flaggedLocationCount = useMemo(
    () => new Set(flaggedNotes.map((note) => note.spaceGroupName)).size,
    [flaggedNotes]
  )

  const flaggedItemColumns = useMemo(
    () => [
      {
        key: "spaceName",
        header: "Space",
        width: "140px",
        cardRole: "title",
        cardOrder: 0,
        render: (row) => (
          <div className="flex items-center gap-2">
            <i className="icon icon-m-flag text-base text-red-400" />
            <span>{row.spaceName}</span>
          </div>
        ),
        cardRender: (row) => (
          <div className="flex items-center gap-2">
            <i className="icon icon-m-flag text-base text-red-400" />
            <span>{row.spaceName}</span>
          </div>
        ),
      },
      {
        key: "vesselName",
        header: "Boat",
        width: "180px",
        cardRole: "subtitle",
        cardOrder: 0,
        render: (row) =>
          row.boat ? (
            <BoatPreviewLink boat={row.boat} />
          ) : (
            row.vesselName || "No boat"
          ),
        cardRender: (row) =>
          row.boat ? (
            <BoatPreviewLink boat={row.boat} />
          ) : (
            row.vesselName || "No boat"
          ),
      },
      {
        key: "note",
        header: "Description",
        width: "280px",
        cardRole: "footer",
        cardOrder: 0,
        cardRender: (row) =>
          row.note ? (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Description
              </div>
              <div className="mt-1 text-sm text-gray-900">{row.note}</div>
            </div>
          ) : null,
      },
      {
        key: "spaceGroupName",
        header: "Space Group",
        width: "180px",
        cardRole: "detail",
        cardOrder: 0,
      },
      {
        key: "startDate",
        header: "Start Date",
        width: "130px",
        cardRole: "detail",
        cardOrder: 2,
      },
      {
        key: "endDate",
        header: "End Date",
        width: "130px",
        cardRole: "detail",
        cardOrder: 3,
      },
      {
        key: "createdAt",
        header: "Flagged",
        width: "160px",
        cardRole: "detail",
        cardOrder: 1,
      },
      {
        key: "status",
        header: "Status",
        width: "120px",
        render: (row) => {
          const display = titlecase(row.status)
          return <Badge color={statusBadgeColor(display)} text={display} />
        },
        cardRole: "badge",
        cardOrder: 0,
        cardRender: (row) => {
          const display = titlecase(row.status)
          return (
            <Badge
              color={statusBadgeColor(display)}
              text={display}
              variant="small"
            />
          )
        },
      },
    ],
    []
  )
  const flaggedItemActions = useMemo(
    () => [
      {
        label: "Resolve",
        icon: "contract-circle-check",
        onClickSingle: (row) => resolveFlagMutation.mutate(row.id),
        confirm: {
          title: "Resolve flag",
          message: (row) => `Resolve this flag on ${row.spaceName}?`,
          confirmLabel: "Resolve",
        },
      },
    ],
    [resolveFlagMutation]
  )
  const flagsRaisedCount = summaryCards.flagsRaised?.count || 0
  const hasFlagsRaised = flagsRaisedCount > 0

  if (summaryQuery.isLoading && !summary) {
    return (
      <div className="p-4">
        <Loader name="dockwalk summary" />
      </div>
    )
  }

  if (summaryQuery.isError) {
    return (
      <div className="p-4">
        <AlertBanner type="error" title="Unable to load dockwalk summary">
          We could not load the dockwalk summary for this date.
        </AlertBanner>
      </div>
    )
  }

  return (
    <div className="min-w-0 max-w-full space-y-6 rounded border border-l-0 border-r-0 p-4">
      <div className="grid min-w-0 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="min-w-0 bg-white p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Spaces Checked
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900">
            {summaryCards.slipsChecked?.checked || 0} /{" "}
            {summaryCards.slipsChecked?.total || 0}
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full bg-blue-600"
              style={{ width: `${summaryCards.slipsChecked?.percent || 0}%` }}
            />
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {summaryCards.slipsChecked?.percent || 0}% complete
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            if (hasFlagsRaised) setIsFlaggedItemsOpen(true)
          }}
          className={`flex min-w-0 flex-col items-start rounded border bg-white p-4 text-left shadow-sm transition-colors ${
            hasFlagsRaised
              ? "border-red-300 hover:border-red-400"
              : "border-gray-300"
          }`}
          disabled={!hasFlagsRaised}
          aria-label={
            hasFlagsRaised
              ? "Open flagged items section"
              : "Flags raised summary"
          }
        >
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Flags Raised
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900">
            {flagsRaisedCount}
          </div>
        </button>

        <div className="min-w-0 rounded border bg-white p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Walks Completed
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900">
            {summaryCards.walksCompleted?.count || 0}
          </div>
        </div>

        <div className="min-w-0 rounded border bg-white p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Avg Walk Time
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900">
            {summaryCards.avgWalkTime?.display || "0m"}
          </div>
        </div>
      </div>

      {summary?.emptyState ? (
        <div className="rounded border border-dashed bg-white p-8 text-center shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900">
            No dockwalk activity yet
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            Encourage your team to complete a dockwalk from the mobile app so
            managers can review checks, flags, and walkthrough progress here.
          </p>
          <a
            href={summary.helpArticleUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex text-sm font-medium text-blue-700 hover:underline"
          >
            Learn more about dockwalks
          </a>
        </div>
      ) : (
        <>
          <CollapsibleBar
            title="Flagged Items"
            badge={<CountBadge count={flaggedNotes.length} />}
            summary={
              <SummaryItems
                items={[
                  {
                    value: flaggedNotes.length,
                    label: pluralize("open flag", flaggedNotes.length),
                  },
                  {
                    value: flaggedLocationCount,
                    label: pluralize("location", flaggedLocationCount),
                  },
                ]}
              />
            }
            open={isFlaggedItemsOpen}
            onOpenChange={setIsFlaggedItemsOpen}
            className="shadow-sm"
            ariaLabel="Toggle flagged items section"
          >
            <div className="min-w-0 overflow-x-auto">
              <DataTableV2
                name="dockwalkFlaggedItems"
                columns={flaggedItemColumns}
                data={flaggedNotes}
                actions={flaggedItemActions}
                hideSettings
                maxHeight="none"
                rowCount={5}
                searchConfig={{
                  keys: ["spaceName", "spaceGroupName", "vesselName", "note"],
                  placeholder: "Search by space, boat, group, or note...",
                }}
                emptyState={
                  <div className="text-sm text-gray-500">
                    No flagged items for this date.
                  </div>
                }
              />
            </div>
          </CollapsibleBar>

          <CollapsibleBar
            title="Walk Progress"
            summary={
              <SummaryItems
                items={[
                  {
                    value: `${summaryCards.slipsChecked?.checked || 0}/${
                      summaryCards.slipsChecked?.total || 0
                    }`,
                    label: `${pluralize(
                      "space",
                      summaryCards.slipsChecked?.total || 0
                    )} checked`,
                  },
                  {
                    value: `${summaryCards.walksCompleted?.count || 0}/${
                      walkProgress.length
                    }`,
                    label: `${pluralize("walk", walkProgress.length)} complete`,
                  },
                ]}
              />
            }
            defaultOpen
            className="shadow-sm"
            ariaLabel="Toggle walk progress section"
          >
            <div className="space-y-4">
              {walkProgress.map((group) => (
                <div key={group.spaceGroupId}>
                  <div className="mb-2 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                      <i
                        className={`icon ${
                          group.complete
                            ? "icon-check text-teal-600"
                            : "icon-circle-o text-gray-400"
                        }`}
                      />
                      <span>{group.spaceGroupName}</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {group.checked} / {group.total}
                    </div>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                    <div
                      className={`h-full ${
                        group.complete ? "bg-teal-600" : "bg-blue-600"
                      }`}
                      style={{ width: `${group.percent}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleBar>

          <WalkActivityLog
            marinaSlug={marinaSlug}
            date={date}
            spaceGroups={spaceGroups}
            slipsChecked={summaryCards.slipsChecked}
            walksCompleted={summaryCards.walksCompleted?.count || 0}
            totalWalks={walkProgress.length}
            flagsRaised={summaryCards.flagsRaised?.count || 0}
          />

          <AuditTrailTable marinaSlug={marinaSlug} date={date} />
        </>
      )}
    </div>
  )
}

DockwalkSummary.propTypes = {
  marinaSlug: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
}

export default DockwalkSummary
